import { Helmet } from 'react-helmet-async'
import { Bar, BarChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { InformationCircleIcon } from '@heroicons/react/24/solid'

import { Link } from '../navigation'
import { plusPeriod } from '../utils/dates'
import { colors, CRITICAL_COLOR, TOO_LATE_COLOR, patent_family, family_member, commodity, design_family, design_registration, trade_mark } from '../data'
import { useTasks } from '../tasks/TasksProvider'
import { TaskProps, dueDateDistance } from '../tasks/utils'
import { ChartContainer, TasksCard } from '../cockpit/Graphs'
import { CockpitPanelsProvider } from '../cockpit/CockpitPanelsProvider'
import { IpManagementCostsPanel } from './IpManagementCostsPanel'
import { IpManagementForecastPanel } from './IpManagementForecastPanel'
import { useRoles } from '../user/Auth'
import RenewalsPipelineChart from '../renewal/RenewalsPipelineChart'
import { useFilteredPatents } from '../filter/FilteredPatents'
import { useFilteredTrademarks } from '../filter/FilteredTrademarks'
import { useFilteredDesigns } from '../filter/FilteredDesigns'
import { usePatents } from '../patents/PatentsProvider'
import { useTrademarks } from '../trademarks/TrademarksProvider'
import { useProducts } from '../products/ProductsProvider'
import { useDesigns } from '../designs/DesignsProvider'
import { useInventions } from '../inventions/InventionsProvider'
import { useUserSettings } from '../user/UserSettingsProvider'

export default function IpManagementCockpit() {
    const { t } = useTranslation()
    const { hasTasks } = useRoles()
    return (
        <>
            {/* @ts-ignore */}
            <Helmet>
                <title>IP Management Cockpit | Patent Cockpit</title>
            </Helmet>
            <CockpitPanelsProvider cockpitType="IpManagement">
                <div className="main-content bg-pcx-100 pt-4">
                    <div className='flex flex-wrap gap-4 h-fit'>
                        <TasksPipelineChart />
                        <RenewalsPipelineChart />
                        <IpManagementCostsPanel panelType="IpManagement" panelId={2} panelName="Yearly IP Management Costs" />
                        <IpManagementForecastPanel panelType="IpManagement" panelId={3} panelName="Total forecast" />
                        <TotalOverviewPanel />
                        {hasTasks && <TasksCard />}
                    </div>
                </div>
            </CockpitPanelsProvider>
        </>
    )
}

type EnrichedTask = TaskProps & { reference?: string, assignee?: string }

const PAST_MONTHS = 2
const FUTURE_MONTHS = 12

type Bucket = {
    key: string
    start: Date
    end: Date
    label: string
    yearLabel: string
    tooLate: number
    critical: number
    soon: number
    tooLateTasks: EnrichedTask[]
    criticalTasks: EnrichedTask[]
    soonTasks: EnrichedTask[]
}

const AXIS_COLOR = '#666'
const TICK_FONT_SIZE = 11
const Y_AXIS_WIDTH = 24
const CHART_MARGIN = { top: 4, right: 6, left: 0, bottom: 0 }

function resolveReference(
    task: TaskProps,
    familyById: Record<number, any>,
    memberById: Record<number, any>,
    trademarkById: Record<number, any>,
    commodityById: Record<number, any>,
    designFamilyById: Record<number, any>,
    designRegistrationById: Record<number, any>,
): string | undefined {
    const { entity, entityId } = task
    if (entity === undefined || entityId === undefined) return undefined
    if (entity === patent_family) return familyById[entityId]?.internalReference
    if (entity === family_member) return memberById[entityId]?.internalReference
    if (entity === trade_mark) return trademarkById[entityId]?.reference
    if (entity === commodity) return commodityById[entityId]?.commodityReference
    if (entity === design_family) return designFamilyById[entityId]?.reference
    if (entity === design_registration) return designRegistrationById[entityId]?.reference
    return undefined
}

function TaskLine({ task, color }: { task: EnrichedTask, color?: string }) {
    const ref = task.reference ? `${task.reference}: ` : ''
    const text = `${ref}${task.title ?? ''}`
    return (
        <div
            className="leading-snug whitespace-nowrap overflow-hidden text-ellipsis"
            title={text}
            style={color ? { color } : undefined}
        >
            {text}
        </div>
    )
}

function PipelineTooltip({ active, payload, buckets }: {
    active?: boolean
    payload?: { payload?: { key?: string } }[]
    buckets: Bucket[]
}) {
    if (!active || !payload?.length) return null
    const bucket = buckets.find(b => b.key === payload[0]?.payload?.key)
    if (!bucket) return null
    const groups = [
        { label: 'Too late', color: TOO_LATE_COLOR, tasks: bucket.tooLateTasks },
        { label: 'Critical', color: CRITICAL_COLOR, tasks: bucket.criticalTasks },
        { label: 'Soon', color: colors['pcx-300'], tasks: bucket.soonTasks },
    ].filter(g => g.tasks.length > 0)
    if (groups.length === 0) return null
    return (
        <div className="bg-white border border-pcx-300 rounded shadow p-2 text-xs max-w-[40rem] flex flex-col gap-1">
            {groups.map(g => (
                <div key={g.label}>
                    {g.tasks.map((task, i) => <TaskLine key={i} task={task} color={g.color} />)}
                </div>
            ))}
        </div>
    )
}

/** Year markers ('26, '27) rendered as HTML below the chart, aligned to the bars by
 *  matching the chart's left inset (Y_AXIS_WIDTH) and right margin. Keeps the two-line
 *  axis without a custom SVG tick. */
function PipelineYearRow({ data }: { data: { key: string, yearLabel: string }[] }) {
    return (
        <div
            className="flex -mt-0.5"
            style={{ paddingLeft: Y_AXIS_WIDTH + CHART_MARGIN.left, paddingRight: CHART_MARGIN.right, fontSize: TICK_FONT_SIZE, color: AXIS_COLOR }}
        >
            {data.map(d => <div key={d.key} className="flex-1 text-center">{d.yearLabel}</div>)}
        </div>
    )
}

function TasksPipelineChart() {
    const { t } = useTranslation()
    const { tasks } = useTasks()
    const { familyById, memberById } = usePatents()
    const { trademarkById } = useTrademarks()
    const { commodityById } = useProducts()
    const { familyById: designFamilyById, registrationById: designRegistrationById } = useDesigns()
    const { users } = useUserSettings()

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const buckets: Bucket[] = _.range(-PAST_MONTHS, FUTURE_MONTHS).map(i => {
        const start = new Date(today.getFullYear(), today.getMonth() + i, 1)
        const end = new Date(today.getFullYear(), today.getMonth() + i + 1, 1)
        const label = start.toLocaleDateString(undefined, { month: 'long' }).charAt(0)
        const isFirst = i === -PAST_MONTHS
        const yearLabel = (isFirst || start.getMonth() === 0) ? `'${String(start.getFullYear()).slice(-2)}` : ''
        const key = `m${i + PAST_MONTHS}`
        return { key, start, end, label, yearLabel, tooLate: 0, critical: 0, soon: 0, tooLateTasks: [], criticalTasks: [], soonTasks: [] }
    })

    const activeTasks = tasks.filter(t => !t.done)

    const pipelineTasks: (EnrichedTask & { bucketLabel?: string, bucketYear?: number, category?: 'tooLate' | 'critical' | 'soon' })[] = []

    for (const task of activeTasks) {
        const dueDate = task.dueDate
        if (!dueDate) continue
        const due = new Date(dueDate)
        const bucket = buckets.find(b => due >= b.start && due < b.end)
        if (!bucket) continue
        const dist = dueDateDistance(task.dueDate, task.done, today)
        const reference = resolveReference(task, familyById, memberById, trademarkById, commodityById, designFamilyById, designRegistrationById)
        const assignee = task.assignedTo ? (users[task.assignedTo]?.displayName ?? task.assignedTo) : undefined
        const enriched: EnrichedTask = { ...task, reference, assignee }

        if (dist === 'too-late') {
            bucket.tooLate++
            bucket.tooLateTasks.push(enriched)
            pipelineTasks.push({ ...enriched, bucketLabel: bucket.label, bucketYear: due.getFullYear(), category: 'tooLate' })
        } else if (dist === 'critical') {
            bucket.critical++
            bucket.criticalTasks.push(enriched)
            pipelineTasks.push({ ...enriched, bucketLabel: bucket.label, bucketYear: due.getFullYear(), category: 'critical' })
        } else {
            bucket.soon++
            bucket.soonTasks.push(enriched)
            pipelineTasks.push({ ...enriched, bucketLabel: bucket.label, bucketYear: due.getFullYear(), category: 'soon' })
        }
    }

    const data = buckets.map(b => ({
        key: b.key,
        label: b.label,
        yearLabel: b.yearLabel,
        tooLate: b.tooLate,
        critical: b.critical,
        soon: b.soon,
    }))

    return (
        <ChartContainer panelType="IpManagement" panelId={1} panelName="Tasks pipeline">
            <div className="flex flex-row justify-between items-baseline mb-4 pr-7">
                <h2>
                    <Link to="/tasks">{t('cockpit-page.tasks-pipeline')}</Link>
                </h2>
                <TasksPipelineInfo tasks={pipelineTasks} data={data} />
            </div>
            <div>
                <ResponsiveContainer className='text-sm' width="100%" height={200}>
                    <BarChart data={data} margin={CHART_MARGIN}>
                        <XAxis dataKey="key" interval={0} height={18} tickLine={false} tick={{ fontSize: TICK_FONT_SIZE, fill: AXIS_COLOR }} tickFormatter={(_v: any, i: number) => data[i]?.label ?? ''} />
                        <YAxis width={Y_AXIS_WIDTH} allowDecimals={false} />
                        <ReferenceLine x={`m${PAST_MONTHS}`} stroke={AXIS_COLOR} strokeWidth={2} ifOverflow="extendDomain" />
                        <Tooltip
                            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                            wrapperStyle={{ outline: 'none', zIndex: 10 }}
                            content={<PipelineTooltip buckets={buckets} />}
                        />
                        <Bar name="Too late" dataKey="tooLate" stackId="a" fill={TOO_LATE_COLOR} />
                        <Bar name="Critical" dataKey="critical" stackId="a" fill={CRITICAL_COLOR} />
                        <Bar name="Soon" dataKey="soon" stackId="a" fill={colors['pcx-300']} />
                    </BarChart>
                </ResponsiveContainer>
                <PipelineYearRow data={data} />
            </div>
            <div className="flex flex-col gap-2 text-slate-500 text-xs w-full pl-2 mt-2">
                <div className="inline-flex gap-2 items-center"><div className="h-4 w-4" style={{ background: TOO_LATE_COLOR }} /> Too late</div>
                <div className="inline-flex gap-2 items-center"><div className="h-4 w-4" style={{ background: CRITICAL_COLOR }} /> Critical (within 14 days)</div>
                <div className="inline-flex gap-2 items-center"><div className="h-4 w-4" style={{ background: colors['pcx-300'] }} /> Soon / further out</div>
            </div>
        </ChartContainer>
    )
}

function TasksPipelineInfo({ tasks, data }: {
    tasks: (EnrichedTask & { bucketLabel?: string, bucketYear?: number, category?: 'tooLate' | 'critical' | 'soon' })[],
    data: { key: string, label: string, yearLabel: string, tooLate: number, critical: number, soon: number }[],
}) {
    return (
        <Popover className="relative">
            <PopoverButton className="text-pcx-500/30 hover:text-pcx-500 rounded-full size-5 text-center">
                <InformationCircleIcon />
            </PopoverButton>
            <PopoverPanel anchor="bottom end" className="p-3 bg-white shadow-lg rounded-md border border-pcx-300 z-30 w-[44rem] max-w-[90vw]">
                <div className="text-xs text-slate-500 mb-2">
                    Tasks considered for the pipeline. "Turns red" is the date a task moves into the <span style={{ color: TOO_LATE_COLOR }}>too-late</span> bucket (the day after its external due date).
                </div>
                <div className="mb-3">
                    <ResponsiveContainer className="text-sm" width="100%" height={150}>
                        <BarChart data={data} margin={CHART_MARGIN}>
                            <XAxis dataKey="key" interval={0} height={18} tickLine={false} tick={{ fontSize: TICK_FONT_SIZE, fill: AXIS_COLOR }} tickFormatter={(_v: any, i: number) => data[i]?.label ?? ''} />
                            <YAxis width={Y_AXIS_WIDTH} allowDecimals={false} />
                            <ReferenceLine x={`m${PAST_MONTHS}`} stroke={AXIS_COLOR} strokeWidth={2} ifOverflow="extendDomain" />
                            <Bar name="Too late" dataKey="tooLate" stackId="a" fill={TOO_LATE_COLOR} />
                            <Bar name="Critical" dataKey="critical" stackId="a" fill={CRITICAL_COLOR} />
                            <Bar name="Soon" dataKey="soon" stackId="a" fill={colors['pcx-300']} />
                        </BarChart>
                    </ResponsiveContainer>
                    <PipelineYearRow data={data} />
                </div>
                <table className="text-xs">
                    <thead>
                        <tr className="pc-header-row">
                            <th className="pc-header-cell">Month</th>
                            <th className="pc-header-cell">Reference</th>
                            <th className="pc-header-cell">Title</th>
                            <th className="pc-header-cell">Internal due</th>
                            <th className="pc-header-cell">External due</th>
                            <th className="pc-header-cell">Turns red</th>
                            <th className="pc-header-cell">Why</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.length === 0 && (
                            <tr><td colSpan={7} className="py-2 text-slate-400">No tasks in the pipeline.</td></tr>
                        )}
                        {tasks.map((task, i) => {
                            const monthName = task.dueDate
                                ? new Date(task.dueDate).toLocaleDateString(undefined, { month: 'long' })
                                : null
                            const colorReason = task.category === 'tooLate'
                                ? 'Past its due date'
                                : task.category === 'critical'
                                    ? 'Due within 14 days from today'
                                    : 'Due more than 14 days out'
                            const why = monthName
                                ? `External due falls in ${monthName} ${task.bucketYear ?? ''}. ${colorReason}.`
                                : colorReason
                            return (
                                <tr key={i} className="border-b border-pcx-100 last:border-b-0">
                                    <td className="pc-cell whitespace-nowrap font-semibold text-pcx-700">{task.bucketLabel ?? '-'}</td>
                                    <td className="pc-cell whitespace-nowrap">{task.reference ?? '-'}</td>
                                    <td className="pc-cell">{task.title ?? '-'}</td>
                                    <td className="pc-cell whitespace-nowrap tabular-nums">{task.internalDueDate ?? '-'}</td>
                                    <td className="pc-cell whitespace-nowrap tabular-nums">{task.dueDate ?? '-'}</td>
                                    <td className="pc-cell whitespace-nowrap tabular-nums">
                                        {task.dueDate ? plusPeriod(task.dueDate, { days: 1 }) : '-'}
                                    </td>
                                    <td className="pc-cell text-slate-600">{why}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </PopoverPanel>
        </Popover>
    )
}

function TotalOverviewPanel() {
    const { t } = useTranslation()
    const { hasInnovation, hasBrands, hasDesigns } = useRoles()
    const { members: patents } = useFilteredPatents()
    const { trademarks } = useFilteredTrademarks()
    const { designRegistrations: designs } = useFilteredDesigns()
    const { inventions } = useInventions()
    const { commodities: products } = useProducts()

    const values = [
        { count: patents.length, label: t('patents'), to: '/patents/portfolio' },
        hasBrands && { count: trademarks.length, label: t('trademarks'), to: '/trademarks/portfolio' },
        hasDesigns && { count: designs.length, label: t('designs'), to: '/designs/portfolio' },
        hasInnovation && { count: inventions.length, label: t('inventions'), to: '/inventions/portfolio' },
        { count: products.length, label: t('products'), to: '/products' },
    ].filter(Boolean) as { count: number; label: string; to: string }[]

    return (
        <ChartContainer panelType="IpManagement" panelId={4} panelName="Total overview">
            <h2 className='mb-2'>{t('cockpit-page.total-overview')}</h2>
            <table>
                <tbody>
                    {values.map(({ count, label, to }) => (
                        <tr key={label}>
                            <td className="pt-4 align-bottom text-right text-3xl font-bold text-pcx-600">{count}</td>
                            <td className="pl-2 align-bottom text-slate-400 font-base text-xl">
                                <Link to={to}>{label}</Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </ChartContainer>
    )
}
